import { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { Fragment } from 'react';

import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import { ListingPageProps } from 'src/common/types/common/ListingPageProps';
import MainContentPlaceholder from 'src/common/components/MainContentPlaceholder';
import { FavoritesProvider } from 'src/common/providers/FavoritesProvider';
import GdprCompliance from 'src/common/components/GdprCompliance';
import Header from 'src/common/components/Header';
import Layout from 'src/common/components/Layout';
import MetaFields from 'src/listing/components/MetaFields';

import handleApiErrors from 'src/common/utils/handleApiErrors';
import serverGetFavorites from 'src/common/utils/serverGetFavorites';
import serverGetListing, { isListingRedirect } from 'src/common/utils/serverGetListing';
import serverGetUser from 'src/common/utils/serverGetUser';
import serverGetInquiryIndicators from 'src/common/utils/serverGetInquiryIndicators';
import serverGetFilteredOwnerBoats from 'src/listing/utils/serverGetFilteredOwnerBoats';
import withCommonMiddleware from 'src/common/utils/withCommonMiddleware';
import ReviewAndAppLinks from 'src/common/components/ReviewAndAppLinks';
import SubFooter from 'src/common/components/SubFooter';
import FooterLinks from 'src/common/components/FooterLinks';
import Footer from 'src/common/components/Footer';
import prependLocale from 'src/common/utils/prependLocale';
import SearchInputProvider from 'src/common/components/Search/SearchInputProvider';
import handleSecondaryContentApiFailures from 'src/common/utils/handleSecondaryContentApiFailures';
import { getRecentInquiryCookieData } from 'src/trip-cart/utils/serverCalculatePrices';
import SearchHeroVisibilityProvider from 'src/common/components/Search/SearchHeroVisibilityProvider';
import MobileWebViewHeader from 'src/common/components/MobileWebViewHeader';
import serverGetInstabookDates, { getInitialCachedDates } from 'src/trip-cart-deprecated/utils/serverGetInstabookDates';
import { useRequestInfoContext } from 'src/common/providers/RequestInfoProvider';
import { isHashedId } from 'src/common/utils/validators';
import { ADDON_PAYMENT_DUE } from 'src/common/types/common/Addons';
import ensureString from 'src/common/utils/ensureString';
import serverGetListingAddons from 'src/common/utils/serverGetListingAddons';
import ListingMotivatorsProvider from 'src/listing/hooks/useListingPageMotivators';
import { BookingMotivatorsProvider } from 'src/common/providers/useBookingMotivators';
import { loadTranslations } from 'src/common/utils/serverSidePropsHelpers';
import useMounted from 'src/common/hooks/useMounted';
import useGalleryView from 'src/listing/hooks/useGalleryView';

const Listing = dynamic(
  () => import('../../../src/listing/components/Root'),
  { loading: () => <MainContentPlaceholder /> },
);

const ListingPage: NextPage<ListingPageProps> = ({
  user,
  favorites,
  threadId,
  listing,
  tripCartCookieData,
  ownerBoats,
  instabookDates,
  addonsOptional = [],
  addonsLater = [],
}) => {
  const { locale } = useRequestInfoContext();
  const { currency } = useCurrencyContext();
  const relatedListings = listing?.related ?? [];
  const { isGalleryViewOpen } = useGalleryView();
  const isMounted = useMounted();
  const isGalleryOpen = isGalleryViewOpen && isMounted;

  return (
    <Fragment key={`${listing.id}-${currency.code}-${locale}`}>
      <MetaFields
        listing={listing}
        relatedListings={relatedListings}
      />

      <Layout withFixedHeader={false}>
        {/* Only show the Cookie banner if the user is logged in */}
        {!user?.id && <GdprCompliance />}
        <FavoritesProvider value={favorites}>
          <SearchInputProvider>
            <Header fixed={false} withSearch />
            <SearchHeroVisibilityProvider>
              <MobileWebViewHeader
                catLocBreadcrumb={listing.cat_loc_breadcrumb}
              />
              <ListingMotivatorsProvider
                listing={listing}
                tripCartCookieData={tripCartCookieData ?? undefined}
              >
                <BookingMotivatorsProvider
                  boatId={listing.id}
                  bookingMotivators={listing.booking_motivators}
                  initialInstabookDates={instabookDates}
                >
                  <Listing
                    listing={listing}
                    relatedListings={relatedListings}
                    threadId={threadId ?? undefined}
                    ownerBoats={ownerBoats}
                    tripCartCookieData={tripCartCookieData ?? undefined}
                    instabookDates={instabookDates}
                    addonsOptional={addonsOptional}
                    addonsLater={addonsLater}
                  />
                </BookingMotivatorsProvider>
              </ListingMotivatorsProvider>
            </SearchHeroVisibilityProvider>
            {!isGalleryOpen && (
              <Footer extraBottomPaddingMobile>
                <FooterLinks />
                <ReviewAndAppLinks />
                <SubFooter />
              </Footer>
            )}
          </SearchInputProvider>
        </FavoritesProvider>
      </Layout>
    </Fragment>
  );
};

type Params = { listing_id: string };

export const getServerSideProps = handleApiErrors(
  withCommonMiddleware<ListingPageProps, Params>(
    async (context) => {
      const listingId = ensureString(context.params?.listing_id);
      // return a 404 response early if the id doesn't pass basic validation.
      if (!isHashedId(listingId)) {
        return {
          notFound: true,
        };
      }

      const translations = loadTranslations(
        context.locale,
        [
          'listing-page',
          'common',
          'navbar',
          'destination-navigation',
          'boat-categories',
          'location-overrides',
          'footer',
          'search-and-filters',
          'boat-categories',
          'inquiry-success',
        ],
      );

      /**
       * Recent search data is coming from cookies now.
       */
      const defaultCookieData = getRecentInquiryCookieData(context);

      const dates = defaultCookieData?.dates ?? [];

      const cacheInstabookMonths = getInitialCachedDates(defaultCookieData);

      const [
        user,
        favorites,
        inquiryIndicators,
        listing,
        ownerBoats,
        instabookDates,
        addOns,
      ] = await Promise.all([
        serverGetUser(context),
        serverGetFavorites(context).catch(handleSecondaryContentApiFailures),
        serverGetInquiryIndicators(context),
        serverGetListing(context, true, dates?.[0] ?? ''),
        serverGetFilteredOwnerBoats(context),
        serverGetInstabookDates(context, cacheInstabookMonths),
        serverGetListingAddons(context, listingId),
      ]);

      const addonsOptional = addOns.filter((addon) => addon.optional);
      const addonsLater = addOns.filter(
        (addon) => addon.payment_due === ADDON_PAYMENT_DUE.AFTER_TRIP,
      );

      if (isListingRedirect(listing)) {
        return {
          redirect: {
            statusCode: listing.code,
            destination: prependLocale(listing.redirect_url, context.locale),
          },
        };
      }
      if (!listing! || !listing?.id) {
        return {
          notFound: true,
        };
      }

      const activeInquiry = inquiryIndicators.results
        .find((inquiry) => inquiry.listing_id === listingId);

      // Disable per person pricing, not supported
      listing.prices = listing.prices?.filter((tier) => tier.price_unit !== 'person');

      /**
       * Check if the listing has instabook available because there are couple of factors
       * that can affect availability of the instabook trips. Either there is no instabooks
       * available for the listing or listing has new Pricing v2 enabled for now.
       */
      const hasInstabookAvailable = listing.has_available_instabook;

      return {
        props: {
          user,
          favorites,
          threadId: activeInquiry?.thread_id ?? null,
          tripCartCookieData: defaultCookieData ?? null,
          listing,
          instabookDates: hasInstabookAvailable ? instabookDates : [],
          cacheInstabookMonths: hasInstabookAvailable ? cacheInstabookMonths : [],
          ownerBoats: ownerBoats.results,
          addonsOptional,
          addonsLater,
          ...await translations,
        },
      };
    },
  ),
);

export default ListingPage;
